"use client";
import { useTranslations } from 'next-intl';
import Input, { InputTypes } from '../../../form-elements/input/Input';
import style from './login.module.scss';
import { useApiClient } from '@/projectRoot/src/contexts/ApiClientContext';
import Button from '../../../form-elements/button/Button';
import Image from 'next/image';
import { checkAllFieldsImproved, ImprovedErrors, isEmail, isRequired } from '@/projectRoot/src/utils/handlers/validationFormHandler';
import { useReducer, useState } from 'react';
import { writeStorage } from '@rehooks/local-storage';
import { AUTH_IDENTITY_TOKEN } from '@/projectRoot/src/utils/constants/storageKeys';
import { useGlobals } from '@/projectRoot/src/contexts/GlobalsContext';
import { setDefaultErrors, uuid } from '@/projectRoot/src/utils/handlers/miscHandler';

export interface LoginState {
    email: string;
    password: string;
    remember_me: 1 | 0;
    errors: ImprovedErrors<keyof Omit<LoginState, 'errors' | 'disable_submit' | 'remember_me'>>;
    disable_submit: boolean;
}

const initialState: LoginState = {
    email: '',
    password: '',
    remember_me: 0,
    errors: {
        email: {
            message: '',
            passed_validation: false
        },
        password: {
            message: '',
            passed_validation: false
        }
    },
    disable_submit: true
}

type LoginAction =
    { type: "setEmail", payload: string }
    | { type: "setPassword", payload: string }
    | { type: "setRememberMe", payload: 1 | 0 }
    | { type: "setInitialState"}

function reducer(state: LoginState, action: LoginAction): LoginState {
    let stateWithErrors = state;

    switch(action.type){
        case 'setEmail': {
            if(isRequired(action.payload)){
                stateWithErrors.errors.email.passed_validation = false;
                stateWithErrors.errors.email.message = 'error_is_required_field';
                stateWithErrors.disable_submit = true;
            }else if(!isEmail(action.payload)){
                stateWithErrors.errors.email.passed_validation = false;
                stateWithErrors.errors.email.message = 'error_invalid_email_format';
                stateWithErrors.disable_submit = true;
            }else{
                stateWithErrors.errors.email.passed_validation = true;
                stateWithErrors.errors.email.message = '';
                stateWithErrors.disable_submit = checkAllFieldsImproved(stateWithErrors);
            }

            return {...stateWithErrors, email: action.payload};
        }
        case 'setPassword': {
            if(isRequired(action.payload)){
                stateWithErrors.errors.password.passed_validation = false;
                stateWithErrors.errors.password.message = 'error_is_required_field';
                stateWithErrors.disable_submit = true;
            }else{
                stateWithErrors.errors.password.passed_validation = true;
                stateWithErrors.errors.password.message = '';
                stateWithErrors.disable_submit = checkAllFieldsImproved(stateWithErrors);
            }

            return {...stateWithErrors, password: action.payload};
        }
        case 'setRememberMe': {
            stateWithErrors.disable_submit = checkAllFieldsImproved(stateWithErrors);

            return {...stateWithErrors, remember_me: action.payload};
        }
        case 'setInitialState': {
            return { ...initialState, errors: setDefaultErrors(initialState.errors) };
        }
    }
}

interface Props {
    setHeadTitle: (title: string) => void;
    classes?: string[];
}

const Login = ({setHeadTitle, classes = []}: Props) => {
    const t = useTranslations();
    const { apiClient } = useApiClient();
    const {toasts, setToasts} = useGlobals();
    const [state, dispatch] = useReducer(reducer, initialState);
    const [showForgotPassword, setShowForgotPassword] = useState<boolean>(false);

    const onClickShowForgotPasswordForm = (show: boolean) => {
        setShowForgotPassword(show);
        setHeadTitle(t(show ? 'forgotten_password' : 'login'));
    }

    const socialLogin = (driver: string) => {
        window.location.href = `${process.env.NEXT_PUBLIC_SOCIAL_LOGIN_ROUTE}/${driver}/redirect/web?return_url=${window.location.href}`;
    }

    const login = async () => {
        if(state.disable_submit) return;
        try{
            const response = await apiClient.auth().login({
                email: state.email,
                password: state.password,
                remember_me: state.remember_me
            });
            if(response.data){
                writeStorage(AUTH_IDENTITY_TOKEN, { identity_token: response.data.token, expires_at: response.data.expires_at });
                setTimeout(() => {
                    dispatch({type: 'setInitialState'});
                    setToasts([...toasts, {
                        id: uuid(),
                        description: `<p><img style="margin-right: 20px;" src='/img/icons/activated-checkmark.svg' alt='checkmark' />${t('successful_login')}</p>`,
                        initiation_time: new Date().getTime(),
                        duration_time: 10000
                    }]);
                }, 500);
            }
        }catch(err){
            console.log(err);
        }
    }

    const requestResetPassword = async () => {
        try{
            const response = await apiClient.auth().initResetPassword({email: state.email});
            dispatch({ type: 'setInitialState' });

            if(response.status == 200){
                setToasts([...toasts, {
                    id: uuid(),
                    description: `<p><img style="margin-right: 20px;" src='/img/icons/activated-checkmark.svg' alt='checkmark' />${t('forgotten_password_email_sent')}</p>`,
                    initiation_time: new Date().getTime(),
                    duration_time: 10000
                }]);
            } else {
                setToasts([...toasts, {
                    id: uuid(),
                    description: `<p><img style="margin-right: 20px;" src='/img/icons/pending-clock.svg' alt='checkmark' />${t('please_try_again_later')}</p>`,
                    initiation_time: new Date().getTime(),
                    duration_time: 10000
                }]);
            }
        }catch(ex){
            console.log(ex);
            dispatch({ type: 'setInitialState' });
    
            if (ex?.response?.status === 429) {
                setToasts([...toasts, {
                    id: uuid(),
                    description: `<p><img style="margin-right: 20px;" src='/img/icons/pending-clock.svg' alt='checkmark' />${t('Errors.too_many_requests')}</p>`,
                    initiation_time: new Date().getTime(),
                    duration_time: 10000
                }]);
            } else if(ex?.response?.status === 404) {
                setToasts([...toasts, {
                    id: uuid(),
                    description: `<p><img style="margin-right: 20px;" src='/img/icons/fail.svg' alt='checkmark' />${t('customer_doesnt_exist')}</p>`,
                    initiation_time: new Date().getTime(),
                    duration_time: 10000
                }]);
            } else {
                setToasts([...toasts, {
                    id: uuid(),
                    description: `<p><img style="margin-right: 20px;" src='/img/icons/pending-clock.svg' alt='checkmark' />${t('please_try_again_later')}</p>`,
                    initiation_time: new Date().getTime(),
                    duration_time: 10000
                }]);
            }
        }

    }

  return (
    <div className={`${style.loginWindow} ${classes.join(' ')}`}>
        {
            !showForgotPassword
            ?
            <div className={style.login}>
                <p className={style.title}>
                    {t('login_with_email')}
                </p>
                <Input 
                    value={state.email}
                    classes={[style.formGroup]}
                    label_text={t('email')}
                    onInput={(e) => dispatch({type: 'setEmail', payload: e.currentTarget.value})}
                    id='email'
                    error={{
                        has_error: !state.errors.email.passed_validation && state.errors.email.message.length > 0,
                        message: state.errors.email.message.length > 0 ? t(`Errors.${state.errors.email.message}`) : ''
                    }}
                />
                <Input 
                    value={state.password}
                    type={InputTypes.PASSWORD}
                    classes={[style.formGroup]}
                    label_text={t('password')}
                    id='password'
                    onInput={(e) => dispatch({type: 'setPassword', payload: e.currentTarget.value})}
                    error={{
                        has_error: !state.errors.password.passed_validation && state.errors.password.message.length > 0,
                        message: state.errors.password.message.length > 0 ? t(`Errors.${state.errors.password.message}`) : ''
                    }}
                />
                <Input 
                    value={state.remember_me}
                    type={InputTypes.CHECKBOX}
                    classes={[style.checkboxGroup]}
                    label_content={<>{t('remember_me')}</>}
                    onChange={(e) => dispatch({type: 'setRememberMe', payload: state.remember_me == 1 ? 0 : 1})}
                    checked={state.remember_me == 1}
                />
                <p className={style.forgotPasswordLink} onClick={() => onClickShowForgotPasswordForm(true)}>
                    {t('forgot_password')}
                </p>
                <Button 
                    label={t('login')}
                    classes={[style.btn]}
                    onClick={login}
                    style={{opacity: state.disable_submit ? 0.5 : 1}}
                />
                <div className={style.socialLogin}>
                    <p>
                        {t('login_with_social')}
                    </p>
                    <Button label={''} classes={[style.socialBtn]} onClick={() => socialLogin('google')}>
                        <Image src={'/img/google.svg'} width={24} height={24} alt='google' />
                    </Button>
                    <Button label={''} classes={[style.socialBtn]} onClick={() => socialLogin('facebook')}>
                        <Image src={'/img/facebook.svg'} width={24} height={24} alt='facebook' />
                    </Button>
                </div>
            </div>
            :
            <div className={style.forgottenPassword}>
                <p className={style.forgotPasswordLink} onClick={() => onClickShowForgotPasswordForm(false)}>
                    {t('back_to_login')}
                </p>
                <p>
                    {t('forgotten_password_text')}
                </p>
                <Input 
                    value={state.email}
                    classes={[style.formGroup]}
                    onInput={(e) => dispatch({type: 'setEmail', payload: e.currentTarget.value})}
                    label_text={t('email')}
                    error={{
                        has_error: !state.errors.email.passed_validation && state.errors.email.message.length > 0,
                        message: state.errors.email.message.length > 0 ? t(`Errors.${state.errors.email.message}`) : ''
                    }}
                />
                <Button 
                    label={t('send')}
                    classes={[style.btn]}
                    onClick={requestResetPassword}
                    disabled={!isEmail(state.email)}
                />
            </div>
        }
    </div>
  )
};

export default Login;
